import { posthog } from 'posthog-js';
import type { UserProfile } from '@athwab/shared';

type AnalyticsProperties = Record<string, unknown>;

const enabled =
  (import.meta.env.VITE_POSTHOG_ENABLED ?? 'false').toLowerCase() === 'true' &&
  typeof import.meta.env.VITE_POSTHOG_KEY === 'string' &&
  import.meta.env.VITE_POSTHOG_KEY.startsWith('phc_');

const host = import.meta.env.VITE_POSTHOG_HOST ?? 'https://eu.i.posthog.com';

const SENSITIVE_PROPERTY_PATTERNS = [
  /email/i,
  /name/i,
  /prompt/i,
  /password/i,
  /secret/i,
  /token/i,
  /authorization/i,
  /card/i,
  /phone/i,
  /url/i,
];

const sanitizeProperties = (properties: AnalyticsProperties = {}) =>
  Object.fromEntries(
    Object.entries(properties).filter(([key, value]) => {
      if (value === undefined) {
        return false;
      }
      return !SENSITIVE_PROPERTY_PATTERNS.some((pattern) => pattern.test(key));
    })
  );

export const initProductAnalytics = () => {
  if (!enabled || posthog.__loaded) {
    return;
  }

  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: host,
    autocapture: true,
    capture_pageview: false,
    person_profiles: 'identified_only',
    property_denylist: [
      '$current_url',
      '$host',
      '$pathname',
      '$referrer',
      '$referring_domain',
      'email',
      'name',
      'prompt',
    ],
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: 'input, textarea, [contenteditable="true"]',
      compress_events: true,
    },
    before_send: (event) => {
      if (!event?.properties) {
        return event;
      }
      return {
        ...event,
        properties: sanitizeProperties(event.properties),
      };
    },
  });
};

export const captureAnalyticsEvent = (event: string, properties?: AnalyticsProperties) => {
  if (!enabled || !posthog.__loaded) {
    return;
  }
  posthog.capture(event, sanitizeProperties(properties));
};

export const capturePageView = (path: string) => {
  captureAnalyticsEvent('$pageview', {
    path,
  });
};

export const identifyAnalyticsUser = (user: UserProfile) => {
  if (!enabled || !posthog.__loaded) {
    return;
  }
  posthog.identify(user.id, {
    role: user.role,
    tier: user.tier,
    hasTopupCredits: user.topupCredits > 0,
    hasPromoCredits: user.promoCreditsGranted > 0,
    hasSavedPaymentMethod: Boolean(user.wallet?.paymentMethod?.verifiedAt),
    marketingOptIn: user.marketingOptIn,
  });
};

export const resetAnalyticsUser = () => {
  if (!enabled || !posthog.__loaded) {
    return;
  }
  posthog.reset();
};
